<script setup lang="ts">
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import { useTokenType } from '~/composables/useTokenType'
import { TransactionActionModel } from '~/types/model/transaction.model'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'
import BcLink from '~/ui/Link/BcLink.vue'

interface IProps {
  row: TransactionActionModel
}

const props = defineProps<IProps>()

const { isErc20 } = useTokenType(props.row.tokenType)
</script>

<template>
  <div class="transfer-row-cont">
    <span class="text-lg text-primary/40">{{ row.event }}</span>
    <div class="flex items-center gap-x-2">
      <div
        class="relative flex h-12 w-12 shrink-0 items-center justify-center p-1.5"
        :class="[isErc20 ? 'overflow-hidden' : 'rounded-lg border border-primary/40']"
      >
        <img
          class="img"
          :class="isErc20 && 'rounded-full'"
          :src="row.tokenInfo.image"
          alt="token image"
        >
        <span
          v-if="!isErc20"
          class="amount"
        >{{ row.size }}</span>
      </div>
      <div class="flex flex-col gap-y-2 truncate text-lg">
        <BcLink
          :content="row.tokenInfo.name"
          :to="isErc20 ? routes.getTokensDetailPageWithTab(row.tokenInfo.address) :
            routes.getNftDetailPageWithTab(row.tokenType, row.tokenInfo.address)"
        />
        <div class="flex items-center gap-x-1 leading-none text-primary/40">
          <template v-if="isErc20">
            <span class="truncate text-primary">{{ row.size }}</span>{{ row.tokenInfo.symbol }}
          </template>
          <template v-else>
            <BcLink
              v-if="row.tokenId"
              :content="row.tokenName || `#${row.tokenId}`"
              :to="routes.getNftItemDetailPageWithTab(row.tokenType, row.tokenInfo.address, row.tokenId)"
              class="flex-auto basis-1/2 truncate"
            />
          </template>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-x-2 truncate">
      <div class="icon">
        <FontAwesomeIcon
          icon="exchange-alt"
          class="text-primary/40"
        />
      </div>
      <BcLabelContainer
        label="From"
        gap-y="gap-y-1"
        class="truncate"
      >
        <template #content>
          <div class="flex items-center gap-x-1 truncate">
            <AddressCopyLink
              class="w-full truncate text-lg"
              tooltip="click to view address"
              :address="row.fromAddressInfo.address"
              :name="row.fromAddressInfo.name"
              :type="row.fromAddressInfo.addressType"
              is-detail
              hide-eoa-icon
              center-ellipsis
            />
          </div>
        </template>
      </BcLabelContainer>
    </div>

    <div class="flex items-center justify-center">
      <FontAwesomeIcon
        icon="arrow-right"
        class="text-primary/40"
      />
    </div>

    <BcLabelContainer
      label="To"
      gap-y="gap-y-1"
      class="truncate"
    >
      <template #content>
        <div class="flex items-center truncate">
          <AddressCopyLink
            class="w-full truncate text-lg"
            tooltip="click to view address"
            :address="row.toAddressInfo.address"
            :name="row.toAddressInfo.name"
            :type="row.toAddressInfo.addressType"
            is-detail
            hide-eoa-icon
            center-ellipsis
          />
        </div>
      </template>
    </BcLabelContainer>
  </div>
</template>

<style scoped lang="scss">
.transfer-row-cont {
  @apply grid items-center w-full gap-x-2 p-2 relative rounded-lg overflow-hidden;
  grid-template-columns: 160px minmax(300px, 1fr) 1fr 64px 1fr;

  @include md {
    grid-template-columns: 80px minmax(150px, 1fr) 1fr 48px 1fr;
  }

  &::after {
    @apply absolute top-0 left-0 w-full h-full bg-gradient-to-r from-primary/[8%] to-point-color/20 opacity-0 z-[-1];
    content: '';
    transition: opacity .3s ease;
  }

  &:hover {
    &::after {
      @apply opacity-100;
    }
  }

  .icon {
    @apply flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/5 text-base font-bold text-primary/40;
  }

  .img {
    @apply w-full object-cover;
  }

  .token-container {
    @include sm {
      @apply flex-row;
    }
  }
}

.amount {
  @apply text-primary/80 absolute bottom-0 left-1 truncate text-base;

  text-shadow: -2px 0px black, 0px 2px black, 2px 0px black, 0px -2px black;
}
</style>
